import React, { useEffect, useState } from "react";
import { merchantAPI } from "../../Api";
import {
  Container, Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, IconButton, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Alert, FormControl, InputLabel, Select, MenuItem
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";

const ListClients = () => { // 
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editClient, setEditClient] = useState(null);
  const [editData, setEditData] = useState({ name: "", site: "", secret: "", comissionEnabled: false, comissionPercent: 0, category: 'main' });
  const [deleteId, setDeleteId] = useState(null);
  const [alert, setAlert] = useState(null);
  const [draggedIndex, setDraggedIndex] = useState(null);

  const fetchClients = async () => {
    setLoading(true);
    try {
      const res = await merchantAPI.getMerchant();
      // Ожидаем, что res.data = { success: true, data: [...] }
      if (res.data && res.data.success && Array.isArray(res.data.data)) {
        setClients(res.data.data);
      } else {
        setClients([]);
        setAlert(<Alert severity="error">Некорректный ответ сервера</Alert>);
      }
    } catch (e) {
      setClients([]);
      setAlert(<Alert severity="error">Ошибка загрузки клиентов</Alert>);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchClients(); }, []);

  const handleEditOpen = (client) => {
    setEditClient(client);
    setEditData({
      name: client.name,
      site: client.site,
      secret: client.secret,
      comissionEnabled: !!client.comissionEnabled,
      comissionPercent: client.comission != null ? (Number(client.comission) - 1) * 100 : 0,
      category: client.category === 'external' ? 'external' : 'main',
    });
  };

  const handleEditChange = (e) => {
    setEditData({ ...editData, [e.target.name]: e.target.value });
  };

  const handleToggleCommission = (e) => {
    setEditData(prev => ({ ...prev, comissionEnabled: e.target.checked }));
  };

  const handleEditSave = async () => {
    try {
    
      await merchantAPI.updateMerchant(editClient._id, {
        name: editData.name,
        site: editData.site,
        secret: editData.secret,
        comissionEnabled: !!editData.comissionEnabled,
        comissionPercent: Number(editData.comissionPercent || 0),
        category: editData.category,
      });
      setAlert(<Alert severity="success">Мерчант обновлён</Alert>);
      setEditClient(null);
      fetchClients();
    } catch {
      setAlert(<Alert severity="error">Ошибка обновления мерчанта</Alert>);
    }
  };

  const handleDelete = async () => {
    try {
      await merchantAPI.deleteMerchant(deleteId);
      setAlert(<Alert severity="success">Мерчант удалён</Alert>);
      setDeleteId(null);
      fetchClients();
    } catch {
      setAlert(<Alert severity="error">Ошибка удаления мерчанта</Alert>);
    }
  };

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;
    
    const newClients = [...clients];
    const draggedItem = newClients[draggedIndex];
    newClients.splice(draggedIndex, 1);
    newClients.splice(index, 0, draggedItem);
    
    setClients(newClients);
    setDraggedIndex(index);
  };

  const handleDragEnd = async () => {
    if (draggedIndex === null) return;
    
    try {
      const merchantIds = clients.map(c => c._id);
      await merchantAPI.reorderMerchants(merchantIds);
      setAlert(<Alert severity="success">Порядок обновлён</Alert>);
    } catch {
      setAlert(<Alert severity="error">Ошибка изменения порядка</Alert>);
      fetchClients();
    } finally {
      setDraggedIndex(null);
    }
  };

  return (
    <Container maxWidth="sd">
      <Box sx={{ mt: 6 }}>
        <Typography variant="h4" align="center" gutterBottom>Список мерчантов</Typography>
        {alert && <Box mb={2}>{alert}</Box>}
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width="50"></TableCell>
                <TableCell>Название</TableCell>
                <TableCell>Сайт</TableCell>
                <TableCell>Мерчант ID</TableCell>
                <TableCell>ApiKey</TableCell>
                <TableCell>Секрет</TableCell>
                <TableCell>Категория</TableCell>
                <TableCell align="right">Действия</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={7}>Загрузка...</TableCell></TableRow>
              ) : !Array.isArray(clients) || clients.length === 0 ? (
                <TableRow><TableCell colSpan={7}>Нет мерчантов</TableCell></TableRow>
              ) : clients.map((client, index) => (
                <TableRow 
                  key={client._id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragEnd={handleDragEnd}
                  sx={{ 
                    cursor: 'move',
                    opacity: draggedIndex === index ? 0.5 : 1,
                    '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                  }}
                >
                  <TableCell>
                    <DragIndicatorIcon sx={{ color: 'action.active', cursor: 'grab' }} />
                  </TableCell>
                  <TableCell>{client.name}</TableCell>
                  <TableCell>{client.site}</TableCell>
                  <TableCell>{client._id}</TableCell>
                  <TableCell>{client.apikey}</TableCell>
                  <TableCell>{client.secret}</TableCell>
                  <TableCell>{client.category === 'external' ? 'Сторонние' : 'Основные'}</TableCell>
                  <TableCell align="right">
                    <IconButton color="primary" onClick={() => handleEditOpen(client)}><EditIcon /></IconButton>
                    <IconButton color="error" onClick={() => setDeleteId(client._id)}><DeleteIcon /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {/* Диалог редактирования */}
        <Dialog open={!!editClient} onClose={() => setEditClient(null)}>
          <DialogTitle>Редактировать мерчанта</DialogTitle>
          <DialogContent>
            <TextField
              margin="dense"
              label="Название"
              name="name"
              value={editData.name}
              onChange={handleEditChange}
              fullWidth
            />
            <TextField
              margin="dense"
              label="Сайт"
              name="site"
              value={editData.site}
              onChange={handleEditChange}
              fullWidth
            />
            <TextField
              margin="dense"
              label="Пароль"
              name="secret"
              value={editData.secret}
              onChange={handleEditChange}
              fullWidth
            />
            <FormControl fullWidth margin="dense">
              <InputLabel id="merchant-category-edit-label">Категория</InputLabel>
              <Select
                labelId="merchant-category-edit-label"
                name="category"
                value={editData.category || 'main'}
                label="Категория"
                onChange={handleEditChange}
              >
                <MenuItem value="main">Основные</MenuItem>
                <MenuItem value="external">Сторонние</MenuItem>
              </Select>
            </FormControl>
            {/* Индивидуальная комиссия мерчанта */}
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1">Индивидуальная комиссия</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                <Button variant={editData.comissionEnabled ? 'contained' : 'outlined'} onClick={() => setEditData(prev => ({ ...prev, comissionEnabled: !prev.comissionEnabled }))}>
                  {editData.comissionEnabled ? 'Включена' : 'Выключена'}
                </Button>
                <TextField
                  margin="dense"
                  label="Комиссия (%)"
                  name="comissionPercent"
                  type="number"
                  value={editData.comissionPercent}
                  onChange={handleEditChange}
                  inputProps={{ step: 0.1, min: 0 }}
                  disabled={!editData.comissionEnabled}
                />
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditClient(null)}>Отмена</Button>
            <Button variant="contained" onClick={handleEditSave}>Сохранить</Button>
          </DialogActions>
        </Dialog>
        {/* Диалог удаления */}
        <Dialog open={!!deleteId} onClose={() => setDeleteId(null)}>
          <DialogTitle>Удалить мерчанта?</DialogTitle>
          <DialogActions>
            <Button onClick={() => setDeleteId(null)}>Отмена</Button>
            <Button variant="contained" color="error" onClick={handleDelete}>Удалить</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default ListClients;
